import type { CliError } from "./cli-error";
import { COLORS, STATUS_COLORS } from "@/lib/shared/output-constants";
import { stripAnsi } from "@/lib/ansi/strip-ansi";
import { supportsColor } from "@/lib/supports/supports-color";

/**
 * Two audiences, one command set: humans read the aligned text, agents pass
 * `--json` and parse stdout. Everything a command prints goes through here, so
 * `--json` is never partially honoured.
 */
export class CliOutput {
  private jsonMode = false;

  /** Declared once per run, before anything can fail. */
  setJson(enabled: boolean): void {
    this.jsonMode = enabled;
  }

  isJson(): boolean {
    return this.jsonMode;
  }

  json(value: unknown): void {
    process.stdout.write(`${JSON.stringify(value, null, 2)}\n`);
  }

  line(text = ""): void {
    process.stdout.write(`${text}\n`);
  }

  error(text: string): void {
    // Sanitised like everything else: a failure message often carries a
    // session's statusReason, which is agent- or tool-authored text — and an
    // error line is the last thing that should be able to repaint the screen.
    process.stderr.write(`${this.paint(`error: ${this.safe(text)}`, "red")}\n`);
  }

  // Progress a long command owes the user; stderr keeps `--json` stdout whole.
  warn(text: string): void {
    process.stderr.write(`${this.paint(this.safe(text), "yellow")}\n`);
  }

  /**
   * The one place a failure is reported. Under `--json` stdout must be
   * parseable, and it was not: failures went to stderr as prose. Success keeps
   * its bare shape, so nothing that already parses stdout has to change.
   */
  failure(error: CliError): void {
    if (!this.jsonMode) {
      this.error(error.message);
      return;
    }

    this.json({
      ok: false,
      error: {
        ...(error.details.status === undefined
          ? {}
          : { status: error.details.status }),
        code: error.code,
        message: this.safe(error.message),
        ...(error.details.issues ? { issues: error.details.issues } : {}),
        ...(error.details.retryAfterSeconds === undefined
          ? {}
          : { retryAfterSeconds: error.details.retryAfterSeconds }),
      },
      // Inside the envelope, so stdout stays one parseable document.
      ...(error.result === undefined ? {} : { result: error.result }),
    });
  }

  paint(text: string, color: string): string {
    if (!supportsColor(this.jsonMode)) {
      return text;
    }
    return `${COLORS[color] ?? ""}${text}${COLORS.reset}`;
  }

  /**
   * Neutralise terminal control sequences in text the platform gives us.
   *
   * Transcript content, PR titles and question bodies carry model output, tool
   * output and repository contents — none of it trusted. Printed raw, an ESC
   * sequence could repaint or erase what the user already read, or emit an OSC
   * 8 hyperlink pointing somewhere else entirely. Newlines and tabs survive;
   * every other C0/C1 control does not. (`--json` is unaffected: JSON.stringify
   * already escapes control characters.)
   */
  safe(text: string): string {
    return text.replace(/[\u0000-\u0008\u000b-\u001f\u007f-\u009f]/g, "");
  }

  /** `safe`, flattened to one line — anything going into a table cell. */
  cell(text: string): string {
    return this.safe(text).replace(/\s+/g, " ").trim();
  }

  status(status: string): string {
    return this.paint(status, STATUS_COLORS[status] ?? "reset");
  }

  /** Left-aligned two-column block — the shape used by every list command. */
  table(rows: string[][]): void {
    if (rows.length === 0) {
      return;
    }
    const firstRow = rows[0];
    if (firstRow === undefined) {
      return;
    }
    const widths = firstRow.map((_, column) =>
      Math.max(...rows.map((row) => stripAnsi(row[column] ?? "").length)),
    );
    for (const row of rows) {
      const line = row
        .map((cell, column) => {
          const width = widths[column];
          if (width === undefined) {
            return cell;
          }
          const padding = width - stripAnsi(cell).length;
          return column === row.length - 1 ? cell : cell + " ".repeat(padding);
        })
        .join("  ");
      this.line(line.trimEnd());
    }
  }
}
